import {
  useState,
  useEffect,
  useContext,
  useReducer,
  lazy,
  Suspense,
} from 'react';
import { Routes, Route, useSearchParams, useNavigate } from 'react-router-dom';

import {
  appendGTM,
  appendEnphaseGTM,
  appendScript,
  encodeAddress,
  getQueryVariable,
} from './helpers';
import { saveStep } from './helpers';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import AppContext from './contexts/AppContext';
import ConfigContext from './contexts/ConfigContext';
import DataContext from './contexts/DataContext';
import EnphaseContext from './contexts/EnphaseContext';
import deBrandedDomains from './data/debrandedDomains';
import californiaNemProviders from './data/californiaNemProviders';
import Home from './pages/Home';
import Faqs from './pages/Success/Faqs';
import ShortISRFeatureForm from './pages/ShortISRFeatureForm';
import './rewrite.css';

import { DEFAULT_PAGE_COUNT } from './v2/components/PageProgress';
import { type InstallerData } from './v2/components/Installer';
import HomeV2 from './v2/pages/HomeV2';
import { type EnlightenData } from './v2/pages/EnlightenLogin';
import GlobalFunnel from './pages/GlobalFunnel';

import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

const Calculating = lazy(() => import('./pages/Calculating'));
const Sandbox = lazy(() => import('./pages/Sandbox'));
const PhoneDisconnected = lazy(() => import('./pages/PhoneDisconnected'));
const Success = lazy(() => import('./pages/Success'));
const SuccessOrganicFunnel = lazy(() => import('./v2/pages/Success'));
const SuccessGlobal = lazy(() => import('./pages/Success/SuccessGlobal'));
const NotFound = lazy(() => import('./pages/NotFound'));

type valueTypes = string | number | boolean | null;

interface AdditionalFields {
  [key: string]: valueTypes;
}

type Option = {
  name: string;
  value: valueTypes;
  icon?: string;
  iconHover?: string;
};

type Options = Option[];

interface Slide {
  title: string;
  key: string;
  selectedValue: valueTypes;
  allowNullValue?: boolean;
  additionalFields?: AdditionalFields;
  options?: Options;
}

type Slides = Slide[];

export interface LeadReferralData {
  isReferral: boolean;
  referralCode: string | null;
  installer?: InstallerData;
}

declare const google: any;
declare const ga: any;

function App({ useDebrandedVersion }: { useDebrandedVersion: boolean }) {
  const configData = useContext(ConfigContext);
  const {
    isDebrandedVersion,
    data: versionData,
    testName,
    features,
    updateConfigData,
    isGlobal,
    country,
  } = configData;

  const [displayHome, setDisplayHome] = useState(false);
  const [savedEmail, setSavedEmail] = useState('');
  const [lsid, setLsid] = useState(getQueryVariable('lsid'));
  const [phoneUpdated, setPhoneUpdated] = useState(false);
  const [changedState, setChangedState] = useState(false);
  const [googleAPILoaded, setGoogleAPILoaded] = useState(false);
  const [enphaseAccTariffs, setEnphaseAccTariffs] = useState({});
  const [isNemVersion, setIsNemVersion] = useState(false); // TODO: Legacy Nem Card, need to remove related code
  const [usedPanelOnTheRoofFailsafe, setUsedPanelOnTheRoofFailsafe] =
    useState(false);
  const [isNem3, setIsNem3] = useState(false);
  const [startedFunnel, setStartedFunnel] = useState(false);
  const [panelPlacementZoomedOutImage, setPanelPlacementZoomedOutImage] =
    useState(null);
  const [panelPlacementZoomedInImage, setPanelPlacementZoomedInImage] =
    useState(null);
  const [showInitialPopup, setShowInitialPopup] = useState(
    getQueryVariable('b') !== 'NA' ? true : false
  );
  const [visitIsFromUS, setVisitIsFromUS] = useState(false);
  const [successUrl, setSuccessUrl] = useState<string | null>(null);
  const [enlightenData, setEnlightenData] = useState<EnlightenData>({
    sites: [],
    token: null,
  });
  const [siteCalcHasError, setSiteCalcHasError] = useState(false);
  const [isLoadingSiteCalculations, setIsLoadingSiteCalculations] =
    useState(false);
  const [leadReferralData, setLeadReferralData] = useState<LeadReferralData>({
    isReferral: false,
    referralCode: null,
  });
  const [hasElecBillChanged, setHasElecBillChanged] = useState(false);
  const [
    userWillNotSpeakToLocalInstaller,
    setUserWillNotSpeakToLocalInstaller,
  ] = useState(false);

  const [searchParams] = useSearchParams();
  const currentParams = Object.fromEntries(searchParams.entries());
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const dataState = {
    lpWizardId: window.location.pathname.split('/')[1],
    timestamp: Date.now(),
  };

  const dataReducer = (data: any, newData: any) => {
    return newData;
  };
  const [data, dispatchSetData] = useReducer(dataReducer, dataState);
  const updateData = (newData: any) => dispatchSetData(newData);

  const initialDataContext = {
    data,
    updateData,
  };

  // Temporary flag to deploy enphase version using the slf API/calls
  const deployLegacyEnph = process.env.REACT_APP_DEPLOY_LEGACY_ENPH === 'true';

  const getInitialEnphaseData = function (
    useAddress: string,
    useZipCode: string
  ) {
    const geocoder = new google.maps.Geocoder();
    const address =
      useAddress ||
      `${data.selectedAddress}, ${data.selectedState.toUpperCase()} ${
        data.selectedZipCode
      }`;

    geocoder.geocode(
      { address: address },
      async function (results: any, status: any) {
        if (status === 'OK') {
          const latLng = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          };
          setLatLng(latLng);

          // Call accTariffs endpoint
          let timestamp = Date.now();
          // const accTariffsEndpoint = `https://sat.solargraf.com/accTariffs?timestamp=${timestamp}&zip=${
          const accTariffsEndpoint = `${process.env.REACT_APP_SOLARGRAPH}/accTariffs?timestamp=${timestamp}&zip=${
            useZipCode || data.selectedZipCode
          }&lat=${latLng.lat}&lon=${latLng.lng}&addr=${
            useAddress || data.googleFormattedAddress
          }`;
          let token = `${process.env.REACT_APP_SAT_TOKEN}`;
          let response = await fetch(accTariffsEndpoint, {
            method: 'GET',
            headers: {
               Authorization: `Basic ${token}`
            },
          });
          const accTariffs = await response.json();
          setEnphaseAccTariffs(accTariffs);

          updateEnphaseData({
            ...enphaseData,
            latLng,
            accTariffs,
          });

          if (features.showPanelPlacement) {
            getPanelPlacementData(latLng);
          }
        }
      }
    );
  };

  const getDataForNem3 = async function () {
    if (!isNem3) return;

    const accTariffs = enphaseData.accTariffs;

    // Call bill2UsageEndpoint
    let timestamp = Date.now();
    // const bill2UsageEndpoint = `https://sat.solargraf.com/bill2Usage?timestamp=${timestamp}&bill=${
    const bill2UsageEndpoint = `${process.env.REACT_APP_SOLARGRAPH}/bill2Usage?timestamp=${timestamp}&bill=${
      data.bill || data.billButtons
    }&tariffId=${accTariffs.prefTariffId}&accountId=${accTariffs.accountId}`;
    let token = `${process.env.REACT_APP_SAT_TOKEN}`;
    const response = await fetch(bill2UsageEndpoint, {
      method: 'GET',
      headers: {
         Authorization: `Basic ${token}`
      },
    });
    const bill2Usage = await response.json();

    let genPreprocessData: any;

    try {
      // Call genPreprocess endpoint
      // const genPreprocessEndpoint = 'https://sat.solargraf.com/genPreprocess';
      const genPreprocessEndpoint = `${process.env.REACT_APP_SOLARGRAPH}/genPreprocess`;
      let token = `${process.env.REACT_APP_SAT_TOKEN}`;
      const genPreprocessResponse = await fetch(genPreprocessEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
           Authorization: `Basic ${token}`
        },
        body: JSON.stringify({
          timestamp: Date.now(),
          accountId: accTariffs.accountId,
          monthlyUsage: bill2Usage.monthlyUsage,
          modMonthlyUsage: Math.round(bill2Usage.monthlyUsage),
          lseId: accTariffs.prefLseId,
          tariffId: accTariffs.prefTariffId,
          roof_data: [],
        }),
      });

      genPreprocessData = await genPreprocessResponse.json();
      updateEnphaseData({
        ...enphaseData,
        bill2Usage,
        genPreprocessData,
      });
      console.log(genPreprocessData);
    } catch {
      // Fail safe when genPreprocess fails
      console.log('genPreprocess FAILED!!!');
    }
  };

  let panelPlacementTimeout: any = null;
  const onPanelPlacementError = (message: string) => {
    console.log(message);
    updateConfigData({
      ...configData,
      features: { ...features, showPanelPlacement: false },
    });
    setUsedPanelOnTheRoofFailsafe(true);
    if (panelPlacementTimeout) {
      clearTimeout(panelPlacementTimeout);
      panelPlacementTimeout = null;
    }
  };

  const getPanelPlacementData = async function (latLng: {
    lat: number;
    lng: number;
  }) {
    // Call panelPlacement endpoint
    const panelPlacementUrl = 'https://adt.solargraf.com';
    const panelPlacementEndpoint = `${panelPlacementUrl}/getpanel_placement?lat=${latLng.lat}&long=${latLng.lng}&http_method=GET&place_on_existing=0&token_id=${data.timestamp}`;

    let zoomedOutImage: any, zoomedInImage: any;

    try {
      if (panelPlacementTimeout) clearTimeout(panelPlacementTimeout);
      panelPlacementTimeout = setTimeout(() => {
        onPanelPlacementError('Panel placement API FAILED (Timeout Error)');
      }, 40000);

      await fetchEventSource(panelPlacementEndpoint, {
        async onmessage(ev) {
          if (ev.event === 'ADT_Error') {
            onPanelPlacementError('Panel placement API FAILED (ADT_Error)');
            console.log(ev);
          } else if (ev.event === 'ZoomedOutImage') {
            zoomedOutImage = JSON.parse(ev.data);
            setPanelPlacementZoomedOutImage(zoomedOutImage);
            if (zoomedInImage) {
              if (panelPlacementTimeout) {
                clearTimeout(panelPlacementTimeout);
                panelPlacementTimeout = null;
              }
            }
          } else if (ev.event === 'ZoomedInImage') {
            zoomedInImage = JSON.parse(ev.data);
            setPanelPlacementZoomedInImage(zoomedInImage);
            if (zoomedOutImage) {
              if (panelPlacementTimeout) {
                clearTimeout(panelPlacementTimeout);
                panelPlacementTimeout = null;
              }
            }
          }
        },
        onerror: (err) => {
          onPanelPlacementError(
            'Panel placement API FAILED (Fetch Event Source Error)'
          );
          console.log(err);
          throw err; // rethrow to stop the operation
        },
      });
    } catch {
      onPanelPlacementError('Panel placement API FAILED (Unknown error)');
    }
  };

  const enphaseDataReducer = (data: any, newData: any) => {
    return newData;
  };
  const [enphaseData, dispatchSetEnphaseData] = useReducer(
    enphaseDataReducer,
    {}
  );
  const updateEnphaseData = (newData: any) => dispatchSetEnphaseData(newData);

  const initialEnphaseContext = {
    enphaseData,
    updateEnphaseData,
    getInitialEnphaseData,
  };

  const manageCalendlyAppointment = (calendlyData: any) => {
    try {
      if (features.shortISRFeatureForm) {
        setIsShortISRFeatureFormCustomer(true);
      }
      
      setISRAppointmentDetails({
        startTime: '',
        userName: '',
        phoneNumber: '',
      });
      (async () => {
        const [inviteeResponse, eventResponse] = await Promise.all([
          fetch(calendlyData.invitee.uri, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_CALENDLY_API_KEY}`,
            },
          }),
          fetch(calendlyData.event.uri, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_CALENDLY_API_KEY}`,
            },
          }),
        ]);

        const inviteeData = await inviteeResponse.json();
        const eventData = await eventResponse.json();

        const { start_time: startTime } = (eventData as any).resource;
        const { user_name: userName } = (eventData as any).resource
          .event_memberships[0];

        const { name, email } = inviteeData.resource;
        const phoneNumber = inviteeData.resource.questions_and_answers.find(
          (item: any) => item.question.toLowerCase().includes('number')
        )?.answer;
        const zipCode = inviteeData.resource.questions_and_answers.find(
          (item: any) => item.question.toLowerCase().includes('zip')
        )?.answer;

        const eventParams = calendlyData.event.uri.split('/')
        const eventId = eventParams[eventParams.length - 1]

        const requestData: any = {
          country: 'US',
          first_name: name,
          full_name: name,
          phone_number: phoneNumber,
          email,
          zip: zipCode,
          human_check: '',
          xxTrustedFormToken: (
            document.getElementById('xxTrustedFormToken_0') as any
          )?.value,
          xxTrustedFormCertUrl: (
            document.getElementById('xxTrustedFormCertUrl_0') as any
          )?.value,
          xxTrustedFormPingUrl: (
            document.getElementById('xxTrustedFormPingUrl_0') as any
          )?.value,
          lsid: lsid,
          f: testName !== 'default' ? 'ab_test_' + testName : null,
          'meta:enphase_branded': 1,
          appointment_host_name: userName,
          appointmentDate: DateTime.fromISO(startTime).toLocaleString({
            weekday: 'long',
            month: 'long',
            day: '2-digit',
          }),
          appointment_time: DateTime.fromISO(startTime).toLocaleString({
            hour: '2-digit',
            minute: '2-digit',
          }),
          partial_lead_type: 'appointment booking',
          event_id: eventId
        };
        if (requestData.first_name === 'passQC') {
          requestData.zip = 99999;
        }
        const formData = new FormData();
        for (let key in requestData) {
          formData.append(key, requestData[key]);
        }
        const postUrl = `${slfApiUrl}/signup/partial${urlParams}${
          testName !== 'default' ? '&f=ab_test_' + testName : ''
        }`;
        const leadResponse = await fetch(postUrl, {
          method: 'POST',
          body: formData,
        });
        // Dev server returns a html response with warnings, so instead of reading the json response we get the text
        const resultText = await leadResponse.text();
        let result: any = {};
        try {
          result = JSON.parse(resultText);
        } catch (e) {
          const jsonMatches = resultText.match(/{".+"}/g);
          if (jsonMatches) result = JSON.parse(jsonMatches[0]);
        }
        setAppointmentLeadId(result.partial_lead_id);

        setISRAppointmentDetails({ startTime, userName, phoneNumber });
      })();
    } catch (e) {}
  };

  useEffect(() => {
    if (isGlobal) {
      i18n.changeLanguage(country.toLowerCase());
      return;
    }

    const checkCountry = (response: any) => {
      if (
        !response.country ||
        !(typeof response.country === 'string') ||
        response.country.length !== 2 ||
        response.country === 'US'
      ) {
        const root = document.documentElement;
        //root.classList.add('hide-cookie-banner');
        setVisitIsFromUS(true);
      }
      setGeoData(response);
      setDisplayHome(true);
      setLsid(getQueryVariable('lsid'));

      if (isISRState(response.state) || features.shortISRFeatureForm) {
        setShowClickToCall(true);
        setIsISR(true);
      }else{
        setShowClickToCall(true);
      }

      if (features.useUtilityHeadline) {
        window.setGeoUtility = function (utilityResponse: any) {
          if (utilityResponse.utility) {
            updateSelectedOption('provider', utilityResponse.utility);
            updateSelectedOption(
              'addressOwnership',
              response.state,
              'selectedState'
            );
            updateSelectedOption(
              'addressOwnership',
              response.zip,
              'selectedZipCode'
            );

            updateData({
              ...data,
              utilityProvider: utilityResponse.utility,
              selectedCity: response.city,
              selectedState: response.state,
              selectedStateName: response.stateName,
              selectedZipCode: response.zip,
            });
          }
        };

        appendScript(
          `https://geoip.solarleadfactory.ai/utility2/?callback=setGeoUtility&zip=${response.zip}`
        );
      } else {
        updateSelectedOption(
          'addressOwnership',
          response.state,
          'selectedState'
        );
        updateSelectedOption(
          'addressOwnership',
          response.zip,
          'selectedZipCode'
        );

        updateData({
          ...data,
          selectedCity: response.city,
          selectedState: response.state,
          selectedStateName: response.stateName,
          selectedZipCode: response.zip,
        });
      }
    };

    checkCountry(window.countryData);

    // Save initial step
    saveStep(0, data.lpWizardId, testName);

    updateSuccessRedirectUrl();

    getLsidOff();

    if (features.showLanderConcept) {
      const root = document.documentElement;
      root.classList.add('lander-concept');
    }

    // Calendly events listener
    const isCalendlyEvent = (e: any) => {
      return e.data.event && e.data.event.indexOf('calendly') === 0;
    };
    window.addEventListener('message', function (e: any) {
      if (isCalendlyEvent(e)) {
        if (e.data.event === 'calendly.event_scheduled') {
          manageCalendlyAppointment(e.data.payload);
        }
      }
    });
  }, []);

  useEffect(() => {
    getDataForNem3();
  }, [isNem3]);

  const getLsidOff = () => {
    try {
      fetch(
        'https://slf-landing.s3.us-west-2.amazonaws.com/assets/lsid-off.json'
      )
        .then((response) => response.json())
        .then((data) => {
          setLsidOff(data.lsid_off);
        });
    } catch {}
  };

  const [loadedHome, setLoadedHome] = useState(false);
  const [loadedSuccessPage, setLoadedSuccessPage] = useState(false);
  const [loadedFaqsPage, setLoadedFaqsPage] = useState(false);
  const [loadedInitialScripts, setLoadedInitialScripts] = useState(false);
  const [successRedirectUrl, setSuccessRedirectUrl] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [totalPageCount, setTotalPageCount] = useState(DEFAULT_PAGE_COUNT);
  const [deBrandedDomain, setDeBrandedDomain] = useState(deBrandedDomains[0]);
  const [lsidOff, setLsidOff]: [string[], any] = useState([]);
  const [latLng, setLatLng] = useState({ lat: 0, lng: 0 });
  const [geoData, setGeoData] = useState({
    zip: '',
    state: '',
    city: '',
    stateName: '',
  });
  const [urlParams, setUrlParams] = useState(window.location.search);
  const [loadingRoofShade, setLoadingRoofShade] = useState(false);
  const [usedSolarAPI, setUsedSolarAPI] = useState(false);
  const [solarAPILotOfShade, setSolarAPILotOfShade] = useState(false);
  const [selectedNonFamilyHome, setSelectedNonFamilyHome] = useState(null);
  const [showClickToCall, setShowClickToCall] = useState(false);
  const [hideBookAppointment, setHideBookAppointment] = useState(false);
  const [clickedCallASpecialist, setClickedCallASpecialist] = useState(false);
  const [clickedBookAppointment, setClickedBookAppointment] = useState(false);
  const [isISR, setIsISR] = useState(false);
  const [appointmentLeadId, setAppointmentLeadId] = useState(null);
  const [isShortISRFeatureFormCustomer, setIsShortISRFeatureFormCustomer] =
    useState(false);
  const [ISRAppointmentDetails, setISRAppointmentDetails]: any = useState(null);

  const ISRStates = [
    'ca',
    'tx',
    'fl',
    'ga',
    'va',
    'pa',
    'ma',
    'nc',
    'oh',
    'ct',
    'wa',
    'nh',
    'ks',
    'mo',
    'co',
    'ri',
    'md',
    'me',
    'az',
    'tn',
    'de',
    'il',
    'sc',
    'ok',
    'la',
    'nv',
    'ky',
    'in',
    'nm',
    'hi',
    'ia',
    'mi',
    'ne',
    'ut',
    'wi',
    'ar',
    'ny',
    'nj'
  ];
  const isISRState = (state: string) =>
    !leadReferralData.isReferral &&
    !currentParams.referral_code &&
    ISRStates.includes(state?.toLowerCase());

  const getCalendlyUrl = () => {
    const calendlyZoneCodes = {
      pst: 'cxcy-y59-qdm',
      mst: 'ckbw-hnk-cqh',
      cst: 'ck8c-hyn-g8r',
      est: 'ckjc-7md-h8k',
    };

    const localTimeOffset = DateTime.local().offset;
    const mstTimeOffset = DateTime.local().setZone('America/Denver').offset;
    const cstTimeOffset = DateTime.local().setZone('America/Chicago').offset;
    const estTimeOffset = DateTime.local().setZone('America/New_York').offset;

    let userTimeZone;
    switch (localTimeOffset) {
      case mstTimeOffset:
        userTimeZone = calendlyZoneCodes.mst;
        break;
      case cstTimeOffset:
        userTimeZone = calendlyZoneCodes.cst;
        break;
      case estTimeOffset:
        userTimeZone = calendlyZoneCodes.est;
        break;
      default:
        userTimeZone = calendlyZoneCodes.pst;
        break;
    }

    return `https://calendly.com/d/${userTimeZone}/solar-consultation-with-enphase?hide_gdpr_banner=1`;
  };
  const calendlyUrl = getCalendlyUrl();

  useEffect(() => {
    if (
      !loadedInitialScripts &&
      (loadedHome ||
        loadedSuccessPage ||
        loadedFaqsPage ||
        features.shortISRFeatureForm)
    ) {
      if (!isGlobal && !features.shortISRFeatureForm) {
        window.initGoogleMap = () => {
          setGoogleAPILoaded(true);
        };
        const gMapsApiUrl = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&callback=initGoogleMap&v=weekly&libraries=places`;
        appendScript(gMapsApiUrl);
      }

      // const securePrivacyScriptUrl = `https://app.secureprivacy.ai/script/${
      //   useDebrandedVersion
      //     ? '636981063a4ee798705f38e5'
      //     : '6346f7eff31c47124c84b42d'
      // }.js`;
      // appendScript(securePrivacyScriptUrl);

      //appendGTM();

      // A third party script loaded by the Enphase GTM breaks the success page reload,
      // so we remove it for the PDF version as well
      const isPDF =
        currentParams.pdf !== undefined && currentParams.pdf === 'true';
      if (!isDebrandedVersion && !isGlobal && !isPDF) //appendEnphaseGTM();

      if (isGlobal) {
        setLoadedInitialScripts(true);
        return;
      }

      // Register event for AB test
      let ls = getQueryVariable('lead_source');
      if (ls === 'NA') ls = getQueryVariable('lsid');
      if (testName !== 'default') {
        const funnel = 'ab_test_' + testName;
        appendScript(
          'https://d2w7hu1gr02wkx.cloudfront.net/data.json?callback=loadscript&ev=21&t=' +
            testName +
            '&ls=' +
            ls +
            '&ts=' +
            Date.now() +
            '&cid=' +
            data.lpWizardId +
            '&c=' +
            getQueryVariable('c') +
            '&f=' +
            funnel
        );
        if (typeof ga == 'function' && typeof ga.getAll == 'function')
          ga.getAll()[0].send('event', 'ab_test', funnel);
      }
      setLoadedInitialScripts(true);

      const domain = deBrandedDomains.find((dom) =>
        window.location.origin.includes(dom.domain)
      );

      const faviconElement: HTMLAnchorElement | null = document?.getElementById(
        'favicon'
      ) as HTMLAnchorElement;
      if ((isDebrandedVersion || !!domain) && !deployLegacyEnph) {
        if (!!domain) {
          const logoUrl =
            domain.logoUrl || `/assets/img/logo/${domain.domain}.png`;
          setDeBrandedDomain({ ...domain, logoUrl });
        }
        faviconElement!.href =
          'https://understandsolarpower.com/assets/img/favicon.ico';
      } else {
        faviconElement!.href =
          'https://start.enphase.com/form-enph/img/favicon.ico';
      }

      // ev=13
      const tc = getQueryVariable('tracking_code');
      const f = getQueryVariable('f');
      const c = getQueryVariable('c');
      appendScript(
        'https://d2w7hu1gr02wkx.cloudfront.net/data.json?callback=loadscript&ev=13&t=' +
          tc +
          '&ls=' +
          ls +
          '&ts=' +
          Date.now() +
          '&cid=' +
          data.lpWizardId +
          '&c=' +
          c +
          '&f=' +
          f
      );

      // Pixel fire (don't fire for success and faqs pages)
      if (!currentParams.a && !currentParams.faqs) {
        appendScript(
          `https://geoip.solarleadfactory.ai/pixel-fire/${window.location.search}`
        );
      }

    }
  }, [loadedHome, loadedSuccessPage, loadedFaqsPage]);

  useEffect(() => {
    updateSuccessRedirectUrl();
  }, [data, enphaseData, latLng]);

  const updateSuccessRedirectUrl = () => {
    const streetAddress =
      data.selectedAddress &&
      data.selectedCity &&
      !data.selectedAddress
        .toLowerCase()
        .includes(data.selectedCity.toLowerCase())
        ? `${data.selectedAddress}, ${data.selectedCity}`
        : data.selectedAddress;
    const fullAddress = `${
      streetAddress ?? data.selectedCity
    }, ${data.selectedState?.toUpperCase()} ${data.selectedZipCode}`;
    let successUrl = '';

    if (deployLegacyEnph) {
      successUrl = `${window.location.protocol}//${
        window.location.hostname
      }/success_slfcalc_enph-new/?a=${encodeAddress(fullAddress)}&lid=${
        data.leadId
      }&zip=${data.selectedZipCode}`;

      setSuccessRedirectUrl(successUrl);
    } else {
      const usePlacementApiQA = getQueryVariable('placementApi') === 'qa';
      const usePlacementApiParam = usePlacementApiQA ? 'placementApi=qa' : '';

      successUrl = `/?a=${encodeAddress(fullAddress)}${
        (data as any).leadId ? `&lid=${data.leadId}` : ''
      }&lsid=${lsid}&zip=${
        data.selectedZipCode
      }&state=${data.selectedState?.toUpperCase()}&loc_a=${latLng.lat}&loc_b=${
        latLng.lng
      }&token_id=${
        data.timestamp
      }${usePlacementApiParam}&provider=${encodeAddress(
        data.utilityProvider
      )}&features=${getFeaturesForUrl(features)}${
        data.bill || data.billButtons
          ? `&monthlyBill=${data.bill || data.billButtons}`
          : ''
      }&isNem3=${isNem3}${data.fullName ? `&fullName=${data.fullName}` : ''}${
        data.phone ? `&phone=${data.phone}` : ''
      }${data.email ? `&email=${data.email}` : ''}${
        data.isEnphaseCustomer ? '&isEnphCustomer=true' : ''
      }${isISR ? '&isr=true' : ''}`;

      setSuccessRedirectUrl(successUrl);
      setSuccessUrl(successUrl);
    }
  };

  const getFeaturesForUrl = (features: any): string => {
    const activeFeatures: string[] = [];
    Object.keys(features).forEach((key: string) => {
      if (features[key]) activeFeatures.push(key);
    });
    return activeFeatures.join(',');
  };

  const initialSlides: Slides = versionData.formSlides;

  const updateCurrentSlide = (number: any) => setCurrentSlide(number);
  const goToPreviousSlide = () => {
    if (currentSlide > 0) {
      let previousSlideIndex = currentSlide - 1;
      if (usedSolarAPI) {
        const sunlightSlideIndex = getSlideIndex('sunlight');
        if (previousSlideIndex === sunlightSlideIndex) {
          previousSlideIndex = currentSlide - 2;
        }
      }
      updateCurrentSlide(previousSlideIndex);
    }
  };
  const goToNextSlide = () => {
    if (!(currentSlide === slides.length - 1)) {
      let nextSlideIndex = currentSlide + 1;
      if (usedSolarAPI) {
        const sunlightSlideIndex = getSlideIndex('sunlight');
        if (nextSlideIndex === sunlightSlideIndex) {
          nextSlideIndex = currentSlide + 2;
        }
      }
      updateCurrentSlide(nextSlideIndex);
    }
  };

  // currentSlide: used for original funnel / currentPageIndex: used for organic funnel
  useEffect(() => {
    if (!isGlobal && !features.isOrganicFunnel)
      saveStep(currentSlide + 1, data.lpWizardId, testName);
  }, [currentSlide]);

  useEffect(() => {
    if (!isGlobal && features.isOrganicFunnel)
      saveStep(currentPageIndex + 1, data.lpWizardId, testName);
  }, [currentPageIndex]);

  const closeInitialPopup = () => setShowInitialPopup(false);

  const slidesReducer = (slides: any, action: any) => {
    const { key, value, customKey } = action;
    const newSlides: any = [];
    slides.forEach((slide: any) => {
      if (slide.key === key) {
        let newSlide: any;
        if (customKey) {
          newSlide = { ...slide };
          newSlide['additionalFields'][customKey] = value;
        } else {
          newSlide = { ...slide, selectedValue: value };
        }
        newSlides.push(newSlide);
      } else {
        newSlides.push(slide);
      }
    });
    return newSlides;
  };

  const [slides, dispatchSetSlides]: [any, any] = useReducer(
    slidesReducer,
    initialSlides
  );

  const updateSelectedOption = (
    key: any,
    value: any,
    customKey: any = null
  ) => {
    dispatchSetSlides({ key, value, customKey });
  };

  const getSandboxSlides = () => {
    return showClickToCall
      ? versionData.sandboxSlides.filter(
          (slide: any) =>
            !['local_installer', 'answer_phone'].includes(slide.key)
        )
      : versionData.sandboxSlides;
  };

  const getSlideIndex = (
    key: string,
    sandboxSlides: boolean = false
  ): number => {
    let index = 0;
    const sourceSlides = sandboxSlides ? getSandboxSlides() : slides;
    for (const slide of sourceSlides) {
      if (slide.key === key) {
        return index;
      }
      index++;
    }
    return -1;
  };

  const slfApiUrl = isDebrandedVersion
    ? process.env.REACT_APP_SLF_API_URL_DEBRANDED
    : process.env.REACT_APP_SLF_API_URL_ENPHASE;

  const zukoSlug = features.isOrganicFunnel
    ? process.env.REACT_APP_ZUKO_SLUG_ORGANIC_TEST
    : process.env.REACT_APP_ZUKO_SLUG;

  const initialAppContext = {
    currentSlide,
    updateCurrentSlide,
    currentPageIndex,
    setCurrentPageIndex,
    totalPageCount,
    setTotalPageCount,
    goToPreviousSlide,
    goToNextSlide,
    slides,
    updateSelectedOption,
    getSlideIndex,
    showInitialPopup,
    closeInitialPopup,
    urlParams,
    setUrlParams,
    savedEmail,
    setSavedEmail,
    lsid,
    phoneUpdated,
    setPhoneUpdated,
    geoData,
    changedState,
    setChangedState,
    deBrandedDomain,
    enphaseAccTariffs,
    setEnphaseAccTariffs,
    googleAPILoaded,
    deployLegacyEnph,
    isNemVersion,
    loadedHome,
    setLoadedHome,
    setLoadedSuccessPage,
    setLoadedFaqsPage,
    slfApiUrl,
    // zukoSlug,
    getFeaturesForUrl,
    usedPanelOnTheRoofFailsafe,
    setUsedPanelOnTheRoofFailsafe,
    isNem3,
    getDataForNem3,
    startedFunnel,
    setStartedFunnel,
    latLng,
    panelPlacementZoomedOutImage,
    panelPlacementZoomedInImage,
    successUrl,
    enlightenData,
    setEnlightenData,
    siteCalcHasError,
    setSiteCalcHasError,
    isLoadingSiteCalculations,
    setIsLoadingSiteCalculations,
    leadReferralData,
    setLeadReferralData,
    hasElecBillChanged,
    setHasElecBillChanged,
    successRedirectUrl,
    loadingRoofShade,
    setLoadingRoofShade,
    usedSolarAPI,
    setUsedSolarAPI,
    solarAPILotOfShade,
    setSolarAPILotOfShade,
    selectedNonFamilyHome,
    setSelectedNonFamilyHome,
    userWillNotSpeakToLocalInstaller,
    setUserWillNotSpeakToLocalInstaller,
    showClickToCall,
    setShowClickToCall,
    getSandboxSlides,
    hideBookAppointment,
    setHideBookAppointment,
    clickedCallASpecialist,
    setClickedCallASpecialist,
    clickedBookAppointment,
    setClickedBookAppointment,
    isISR,
    setIsISR,
    isISRState,
    appointmentLeadId,
    calendlyUrl,
    isShortISRFeatureFormCustomer,
    setIsShortISRFeatureFormCustomer,
    setGoogleAPILoaded,
    ISRAppointmentDetails,
    setISRAppointmentDetails,
  };

  useEffect(() => {
    localStorage.setItem(
      'slf-app',
      JSON.stringify({
        geoData,
        currentSlide,
        showInitialPopup,
        slides: slides.map((slide: any) => {
          return {
            key: slide.key,
            selectedValue: slide.selectedValue,
            additionalFields: slide.additionalFields,
          };
        }),
      })
    );
  }, [currentSlide, showInitialPopup, slides]);

  useEffect(() => {
    localStorage.setItem('slf-data', JSON.stringify({ ...data }));
  }, [data]);

  useEffect(() => {
    const californiaProviderNames = [
      ...Object.keys(californiaNemProviders),
      ...Object.values(californiaNemProviders),
    ];
    setIsNemVersion(
      features.showNemComparison &&
        (californiaProviderNames.includes(data.utilityProvider) ||
          californiaProviderNames.includes(currentParams.provider))
    );
    setIsNem3(
      californiaProviderNames.includes(data.utilityProvider) ||
        californiaProviderNames.includes(currentParams.provider) ||
        currentParams.isNem3 === 'true'
    );
  }, [data.utilityProvider, currentParams]);

  useEffect(() => {
    if (lsidOff.includes(lsid)) {
      navigate('not-found');
    }

    // Set lead referral metadata
    if (
      currentParams.lsid !== undefined &&
      currentParams.referral_code !== undefined
    ) {
      const referralCode = currentParams.referral_code;
      setLeadReferralData({
        isReferral: true,
        referralCode,
      });
    }
  }, [lsid, lsidOff]);

  const isEnphaseCustomerParam =
    currentParams.isEnphCustomer && currentParams.isEnphCustomer === 'true';
  const showSuccessOrganicFunnel =
    features.isOrganicFunnel &&
    (data.isEnphaseCustomer || isEnphaseCustomerParam);

  return (
    <AppContext.Provider value={initialAppContext}>
      <DataContext.Provider value={initialDataContext}>
        <EnphaseContext.Provider value={initialEnphaseContext}>
          <Suspense
            fallback={
              <div className="fixed left-0 top-0 w-full h-full flex items-center">
                <span className="loader-dots"></span>
              </div>
            }>
            <Routes>
              <Route
                path="/"
                element={
                  !!currentParams.a ? (
                    showSuccessOrganicFunnel ? (
                      <SuccessOrganicFunnel></SuccessOrganicFunnel>
                    ) : (
                      <Success></Success>
                    )
                  ) : !!currentParams.faqs ? (
                    <Faqs></Faqs>
                  ) : !!currentParams.page ? (
                    <HomeV2 page={currentParams.page} />
                  ) : !!currentParams.lang && currentParams.pg === 'success' ? (
                    <SuccessGlobal></SuccessGlobal>
                  ) : !!currentParams.faqs ? (
                    <Faqs></Faqs>
                  ) : isGlobal ? (
                    <GlobalFunnel></GlobalFunnel>
                  ) : features.shortISRFeatureForm ? (
                    <ShortISRFeatureForm></ShortISRFeatureForm>
                  ) : (
                    <Home displayPage={displayHome} />
                  )
                }
              />
              <Route
                path="calculating"
                element={
                  <Calculating
                    title="Calculating..."
                    redirectUrl={`/sb${urlParams}`}
                    postData={true}
                  />
                }
              />
              <Route path="sb" element={<Sandbox />} />
              <Route
                path="processing"
                element={
                  <Calculating
                    title="Processing..."
                    redirectUrl={successRedirectUrl}
                  />
                }
              />
              <Route
                path="phone-disconnected"
                element={<PhoneDisconnected />}
              />
              <Route path="not-found" element={<NotFound />} />
            </Routes>
          </Suspense>
        </EnphaseContext.Provider>
      </DataContext.Provider>
    </AppContext.Provider>
  );
}

export default App;
